import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GroupClassroomContent as Content } from "content";
import { useBreadcrumbs } from "hooks/redux";
import { useModal, useRole } from "hooks/common";
import { useGroupById } from "hooks/api/groups";
import { useCourses } from "hooks/api/courses";
import { Space } from "components/Common/Space";
import { Loader } from "components/Common/Loader";
import { MaterialsPagination } from "components/Courses/MaterialsPagination";
import { MaterialsCoverItem } from "components/Courses/MaterialsCoverItem";
import { GroupClassroomEmpty } from "./components/GroupClassroomEmpty";
import { AddGroupMaterialModal } from "./components/AddGroupMaterialModal";
import { Route } from "constants/routes";
import { TCourse } from "types/api/course";
import "./GroupClassroom.styles.scss";
import { Empty } from "components/Common/Empty";

export const GroupClassroom: React.FC = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();

  const [pageNumber, setPageNumber] = useState<number>(0);

  const { setPageName, resetPageName } = useBreadcrumbs();
  const {
    openModal: openAddMaterialModal,
    closeModal: closeAddMaterialModal,
    isModalOpen: isAddMaterialModalOpen,
  } = useModal(false);

  const { isUserStudent } = useRole();

  const { data: group, isLoading: isGroupLoading } = useGroupById(
    groupId ? parseInt(groupId) : undefined
  );
  const { data: coursesData, isFetching: isCourseLoading } = useCourses({
    pageNumber,
    groupId,
  });

  const courses: TCourse[] = useMemo(
    () => coursesData?.content || [],
    [coursesData?.content]
  );
  const totalPages: number = useMemo(
    () => coursesData?.totalPages || 0,
    [coursesData?.totalPages]
  );
  const isEmpty: boolean = useMemo(() => courses.length === 0, [courses]);

  useEffect(() => {
    if (group?.name) {
      setPageName(group.name);
    }
    return () => resetPageName();
  }, [group?.name, setPageName, resetPageName]);

  const pageChangeHandler = (newPage: number) => {
    setPageNumber(newPage);
  };

  const courseClickHandler = (courseId: number) => {
    if (!groupId) return;
    navigate(
      Route.App.Study.GROUP_CLASSROOM_COURSE.construct(
        groupId,
        courseId.toString()
      )
    );
  };

  return (
    <>
      {isCourseLoading || isGroupLoading ? (
        <Loader />
      ) : isEmpty && !isCourseLoading && !isUserStudent ? (
        <GroupClassroomEmpty onAddClick={openAddMaterialModal} />
      ) : isEmpty && !isCourseLoading && isUserStudent ? (
        <Empty text={Content.EmptyForStudent} />
      ) : (
        <section className="nb-sub-groups-classroom-container">
          <Space
            direction="vertical"
            justify="start"
            align="start"
            size="medium"
            fullWidth
          >
            <ul className="nb-sub-groups-classroom-list">
              {courses.map((course) => (
                <MaterialsCoverItem
                  key={course.id}
                  course={course}
                  onClick={courseClickHandler}
                />
              ))}
            </ul>
            <MaterialsPagination
              currentPage={pageNumber}
              totalPages={totalPages}
              onPageChange={pageChangeHandler}
            />
          </Space>
        </section>
      )}
      <AddGroupMaterialModal
        open={isAddMaterialModalOpen}
        existingCourses={courses}
        onClose={closeAddMaterialModal}
      />
    </>
  );
};
